import { useEffect, useMemo, useState } from "react";

import { MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import { DateTime } from "luxon";

import { ButtonInput } from "../../components/shared/Inputs/ButtonInput";
import LoadingWheel from "../../components/shared/LoadingWheel";
import useNotification from "../../components/notifications/useNotifications";
import { usePageNameContext } from "../../contexts/PageTitleContext";
import { useScannerOverride } from "../../contexts/ScannerContext";
import usePartAssembly from "../../data/usePartAssembly";
import useDismantlingBays from "../../data/useDismantlingBays";
import useProductionPlanner from "../../data/useProductionPlanner";
import useApiHelper from "../../hooks/useApiHelper";
import { DismantlingBayType } from "../../models/DismantlingBay";
import { IAllJobsResponse, IJob } from "../../models/Job";
import { OmniSearchResponse, OmniSearchType } from "../../models/search/OmniSearch";
import JobWorkspace from "../Jobs/JobWorkspace";

// Persist the chosen station per device so a fixed floor terminal stays on its
// assembly between visits.
const STATION_STORAGE_KEY = "stationManagerAssemblyId";

// Bay stations are real DismantlingBay entities. Their station id is the bay id
// behind this prefix so they can't collide with an assembly id and are easy to
// tell apart from the module sentinels.
const BAY_PREFIX = "__bay__:";
const isBayStation = (id: string) => id.startsWith(BAY_PREFIX);
const bayIdFromStation = (id: string) => id.slice(BAY_PREFIX.length);

const readStoredStation = (): string => {
  try {
    return window.localStorage.getItem(STATION_STORAGE_KEY) ?? "";
  } catch {
    return "";
  }
};

// From all jobs for a vehicle, prefer an in-progress (not completed) one,
// otherwise the most recently created.
const pickActiveJob = (jobs: IJob[]): IJob | undefined => {
  if (jobs.length === 0) return undefined;
  const byNewest = [...jobs].sort(
    (a, b) => new Date(b.created).getTime() - new Date(a.created).getTime()
  );
  return byNewest.find((job) => !job.completedOn) ?? byNewest[0];
};

export default function StationManager() {
  const { setInfo } = usePageNameContext();
  const { get } = useApiHelper();
  const { addNotification } = useNotification();
  const { assemblies } = usePartAssembly();
  const { dismantlingBays } = useDismantlingBays();
  const { allocateVehicle } = useProductionPlanner();

  const [stationId, setStationId] = useState<string>(readStoredStation);
  const [reg, setReg] = useState<string>("");
  // The currently selected vehicle, kept across station changes. loadedJobId is
  // its resolved dismantling job (assembly stations only) and is always either
  // null or the job for selectedVehicleId.
  const [selectedVehicleId, setSelectedVehicleId] = useState<string | null>(null);
  const [loadedJobId, setLoadedJobId] = useState<string | null>(null);
  const [isResolving, setIsResolving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // True when the next loaded job should auto-start its timer and scroll down
  // (set when the user re-scans a different vehicle to switch to it).
  const [autoStartTimer, setAutoStartTimer] = useState(false);

  const bayStation = isBayStation(stationId);
  // Whether content is currently loaded for the chosen station.
  const hasLoaded = !!loadedJobId;

  useEffect(() => {
    setInfo({
      name: "Station Manager",
      desc: "Scan a vehicle to view its dismantling job by assembly",
    });
  }, [setInfo]);

  // Persist the station selection.
  useEffect(() => {
    try {
      if (stationId) window.localStorage.setItem(STATION_STORAGE_KEY, stationId);
      else window.localStorage.removeItem(STATION_STORAGE_KEY);
    } catch {
      /* ignore */
    }
  }, [stationId]);

  // Assemblies in display order, matching the rest of the app.
  const stationOptions = useMemo(() => {
    const list = assemblies.data?.data ?? [];
    return [...list].sort((a, b) => {
      const orderA = a.order ?? Number.MAX_SAFE_INTEGER;
      const orderB = b.order ?? Number.MAX_SAFE_INTEGER;
      if (orderA !== orderB) return orderA - orderB;
      return a.name.localeCompare(b.name);
    });
  }, [assemblies.data]);

  // Dismantling bays (type Bay) offered as their own stations, ordered by name.
  const bayStations = useMemo(() => {
    const list = dismantlingBays.data?.data ?? [];
    return list
      .filter((b) => b.type === DismantlingBayType.Bay)
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [dismantlingBays.data]);

  // If a previously stored station no longer exists, clear it. A bay station is
  // valid while its bay still exists.
  useEffect(() => {
    if (assemblies.isLoading || dismantlingBays.isLoading) return;
    if (!stationId) return;
    if (isBayStation(stationId)) {
      if (!bayStations.some((b) => b.id === bayIdFromStation(stationId))) setStationId("");
      return;
    }
    if (!stationOptions.some((a) => a.id === stationId)) setStationId("");
  }, [assemblies.isLoading, dismantlingBays.isLoading, stationOptions, bayStations, stationId]);

  // Resolve a vehicle id to its active job and load it into the workspace.
  // When autoStart is true the loaded job will start its timer and scroll down.
  const loadJobForVehicleId = async (vehicleId: string, autoStart = false) => {
    setIsResolving(true);
    setError(null);
    setSelectedVehicleId(vehicleId);
    try {
      const res = await get<IAllJobsResponse>(`/job/vehicle/${vehicleId}`);
      const job = pickActiveJob(res.data ?? []);
      if (!job) {
        setLoadedJobId(null);
        setError("No dismantling job found for that vehicle.");
        return;
      }
      setAutoStartTimer(autoStart);
      setLoadedJobId(job.id);
    } catch {
      setError("Couldn't load the job for that vehicle.");
    } finally {
      setIsResolving(false);
    }
  };

  // Allocate a vehicle to the given bay station for today, so loading a vehicle
  // at a bay drops it into that bay for the day (mirrors the Production Planner
  // drag/drop). Fire-and-forget: allocation failing shouldn't block viewing the
  // job, so we surface a toast but still load it.
  const allocateToBay = (vehicleId: string, stationValue: string) => {
    const bayId = bayIdFromStation(stationValue);
    if (!bayId) return;
    const plannedOn = DateTime.local().startOf("day").toISODate() ?? "";
    allocateVehicle.mutate(
      { vehicleId, body: { dismantlingBayId: bayId, plannedOn } },
      {
        onError: () =>
          addNotification({
            variant: "error",
            primaryText: "Couldn't allocate the vehicle to this bay.",
          }),
      }
    );
  };

  // Select a vehicle for the current station: an assembly or bay station
  // resolves its dismantling job (and a bay station additionally allocates the
  // vehicle to that bay for today). When startTimer is true the loaded job
  // auto-starts its timer and scrolls down (scan path).
  const selectVehicle = (vehicleId: string, startTimer = false) => {
    if (bayStation) allocateToBay(vehicleId, stationId);
    loadJobForVehicleId(vehicleId, startTimer);
  };

  // A different vehicle was scanned inside the loaded job. Open it in this same
  // station, optionally starting the timer and scrolling down (re-scan path).
  const handleSwitchVehicle = (vehicleId: string, vrm: string | undefined, startTimer: boolean) => {
    if (vrm) setReg(vrm);
    if (bayStation) allocateToBay(vehicleId, stationId);
    loadJobForVehicleId(vehicleId, startTimer);
  };

  // Resolve a typed reg to a vehicle, then to its job.
  const handleLoad = async () => {
    const term = reg.trim();
    if (!stationId) {
      addNotification({ variant: "warning", primaryText: "Pick a station first" });
      return;
    }
    if (!term) return;

    setIsResolving(true);
    setError(null);
    try {
      const res = await get<OmniSearchResponse>(
        `/omnisearch?query=${encodeURIComponent(term)}&type=${OmniSearchType.Vehicle}`
      );
      const vehicle = (res.data ?? []).find((r) => r.type === OmniSearchType.Vehicle) ?? res.data?.[0];
      if (!vehicle) {
        setError(`No vehicle found for "${term}".`);
        setIsResolving(false);
        return;
      }
      if (bayStation) allocateToBay(vehicle.id, stationId);
      await loadJobForVehicleId(vehicle.id);
    } catch {
      setError(`Couldn't find a vehicle for "${term}".`);
      setIsResolving(false);
    }
  };

  // Scanning a vehicle barcode ("v|{vehicleId}|{vrm}") loads it directly by
  // resolving its dismantling job and starts its timer. Once a job is loaded,
  // JobWorkspace registers its own scanner override (most-recent-wins), so this
  // only fires on the picker screen.
  useScannerOverride((scan) => {
    const { parts } = scan;
    if (parts[0]?.toLowerCase() !== "v" || !parts[1]) return;

    if (!stationId) {
      addNotification({ variant: "warning", primaryText: "Pick a station first" });
      return;
    }
    if (parts[2]) setReg(parts[2]);
    selectVehicle(parts[1], true);
  });

  // Switching the chosen station keeps the selected vehicle and re-resolves the
  // content for the new station (an assembly station needs its job).
  const handleStationChange = (value: string) => {
    setStationId(value);
    setError(null);
    setAutoStartTimer(false);
    if (!selectedVehicleId) return;
    // Landing on a bay station with a vehicle already in hand allocates it to
    // that bay (even if its job is already loaded from a previous station).
    if (isBayStation(value)) {
      allocateToBay(selectedVehicleId, value);
      if (!loadedJobId) loadJobForVehicleId(selectedVehicleId);
    } else if (!loadedJobId) {
      loadJobForVehicleId(selectedVehicleId);
    }
  };

  const handleChangeVehicle = () => {
    setSelectedVehicleId(null);
    setLoadedJobId(null);
    setReg("");
    setError(null);
    setAutoStartTimer(false);
  };

  return (
    <div className="mt-4 space-y-4">
      {/* Picker bar */}
      <div className="rounded-lg bg-white p-4 shadow">
        <div className="flex flex-col gap-4 md:flex-row md:items-end">
          <div className="md:w-64">
            <label className="block text-xs font-semibold uppercase tracking-wide text-gray-500">
              Your station
            </label>
            <select
              value={stationId}
              onChange={(e) => handleStationChange(e.target.value)}
              disabled={assemblies.isLoading}
              className="mt-1 w-full rounded-md border-gray-300 text-sm focus:border-primary-500 focus:ring-primary-500"
            >
              <option value="">{assemblies.isLoading ? "Loading…" : "Select a station…"}</option>
              <optgroup label="Stations">
                {stationOptions.map((assembly) => (
                  <option key={assembly.id} value={assembly.id}>
                    {assembly.name}
                  </option>
                ))}
              </optgroup>
              {bayStations.length > 0 && (
                <optgroup label="Bays">
                  {bayStations.map((bay) => (
                    <option key={bay.id} value={`${BAY_PREFIX}${bay.id}`}>
                      {bay.name}
                    </option>
                  ))}
                </optgroup>
              )}
            </select>
          </div>

          <div className="flex-1">
            <label className="block text-xs font-semibold uppercase tracking-wide text-gray-500">
              Scan or enter vehicle reg
            </label>
            <div className="relative mt-1">
              <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
              </div>
              <input
                type="text"
                value={reg}
                disabled={hasLoaded}
                onChange={(e) => setReg(e.target.value.toUpperCase())}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleLoad();
                }}
                placeholder="e.g. KT68XVX"
                className="w-full rounded-md border-gray-300 pl-10 text-sm uppercase focus:border-primary-500 focus:ring-primary-500 disabled:bg-gray-100"
              />
            </div>
          </div>

          <div className="flex items-center gap-x-2">
            {hasLoaded ? (
              <ButtonInput label="Change vehicle" isSubmit={false} onClick={handleChangeVehicle} classes="" />
            ) : (
              <ButtonInput
                label="Load job"
                isSubmit={false}
                onClick={handleLoad}
                isLoading={isResolving}
                disabled={!stationId || !reg.trim()}
                classes=""
              />
            )}
          </div>
        </div>

        {error && <p className="mt-3 text-sm font-medium text-red-600">{error}</p>}
      </div>

      {/* Content for the selected station: a dismantling job for an assembly or
          bay station. */}
      {loadedJobId ? (
        bayStation ? (
          // A bay shows the whole job (all assemblies), like the standalone
          // ViewJob page, since a bay isn't tied to a single assembly.
          <JobWorkspace
            key={loadedJobId}
            jobId={loadedJobId}
            managePageTitle={false}
            onSwitchVehicle={handleSwitchVehicle}
            autoStartTimer={autoStartTimer}
            hideOverview
          />
        ) : (
          <JobWorkspace
            key={loadedJobId}
            jobId={loadedJobId}
            restrictToAssemblyId={stationId || undefined}
            managePageTitle={false}
            onSwitchVehicle={handleSwitchVehicle}
            autoStartTimer={autoStartTimer}
            hideTriage
            collapseDismantlingTimer
            hideOverview
          />
        )
      ) : isResolving ? (
        <LoadingWheel />
      ) : (
        <div className="rounded-lg bg-white p-8 text-center text-gray-500 shadow">
          {!stationId
            ? "Select your station, then scan or enter a vehicle reg."
            : "Scan or enter a vehicle reg to load its dismantling job."}
        </div>
      )}
    </div>
  );
}
